import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { baseModelShape } from '../base-model'
import { organizationShape } from '../user'
import { vendorBankAccountShape } from '../vendor-bank-accounts/models'

// Native enums
export const vendorTypeEnum = {
  INDIVIDUAL: 'individual',
  BUSINESS: 'business',
} as const

export type VendorTypeValues = (typeof vendorTypeEnum)[keyof typeof vendorTypeEnum]

export const vendorTypeLabelMap = {
  [vendorTypeEnum.INDIVIDUAL]: 'Individual',
  [vendorTypeEnum.BUSINESS]: 'Business',
}

export const taxIdTypeEnum = {
  NIT: 'nit',
  RUT: 'rut',
  SSN: 'ssn',
  EIN: 'ein',
  VAT: 'vat',
  RFC: 'rfc',
  OTHER: 'other',
} as const

export type TaxIdTypeValues = (typeof taxIdTypeEnum)[keyof typeof taxIdTypeEnum]

export const taxIdTypeLabelMap = {
  [taxIdTypeEnum.NIT]: 'NIT (Colombia)',
  [taxIdTypeEnum.RUT]: 'RUT (Chile/Colombia)',
  [taxIdTypeEnum.SSN]: 'SSN (US)',
  [taxIdTypeEnum.EIN]: 'EIN (US)',
  [taxIdTypeEnum.VAT]: 'VAT (EU)',
  [taxIdTypeEnum.RFC]: 'RFC (Mexico)',
  [taxIdTypeEnum.OTHER]: 'Other',
}

export const idNumberTypeEnum = {
  PASSPORT: 'passport',
  LICENSE: 'license',
} as const

export type IdNumberTypeValues = (typeof idNumberTypeEnum)[keyof typeof idNumberTypeEnum]

export const idNumberTypeLabelMap = {
  [idNumberTypeEnum.PASSPORT]: 'Passport',
  [idNumberTypeEnum.LICENSE]: 'License',
}

export const vendorCreateShape = {
  name: z.string(),
  displayName: z.string().optional().nullable(),
  dbaNames: z.array(z.string()).optional().nullable(),
  printName: z.string().optional().nullable(),
  vendorType: z.enum(vendorTypeEnum),
  taxId: z.string().optional().nullable(),
  identificationNumber: z.string().optional().nullable(),
  identificationNumberType: z.enum(idNumberTypeEnum).optional().nullable(),
  taxIdType: z.enum(taxIdTypeEnum).optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().optional().nullable(),
  website: z.string().url().optional().nullable(),
  addressLine1: z.string().optional().nullable(),
  addressLine2: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  stateProvince: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  currencyCode: z.string().optional().nullable(),
  requires1099: z.boolean().optional(),
  accountNumber: z.string().optional().nullable(),
  isActive: z.boolean().optional(),
  withholdingRate: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  externalIds: z.record(z.string(), z.string()).optional().nullable(),
  organizations: z.array(z.string()).optional(),
}

export const vendorShape = {
  ...baseModelShape,
  ...vendorCreateShape,
  organizationsRef: z.array(z.object(organizationShape)).optional(),
  bankAccountsRef: z.array(z.object(vendorBankAccountShape)).optional(),
}

export const vendorFilterShape = {
  search: z.string(),
  vendorType: z.array(z.string()),
  isActive: z.boolean(),
  organization: z.array(z.string()),
}

export type Vendor = GetInferredFromRaw<typeof vendorShape>
export type VendorCreate = GetInferredFromRaw<typeof vendorCreateShape>
export type VendorFilter = GetInferredFromRaw<typeof vendorFilterShape>
